/**
 * <product-form> — Ajax add to cart, then opens the cart drawer.
 *
 * Usage:
 *   <product-form>
 *     <form method="post" action="/cart/add"> <input name="id" value="VARIANT_ID"> <input name="quantity" value="1"> <button type="submit">Add</button> </form>
 *   </product-form>
 * Works with any extra fields (properties[…], selling_plan, attributes). Optional hooks inside the element:
 *   [data-product-form-error]  element that receives the error message (created if missing)
 * Attributes: data-no-drawer  -> do not open the drawer (just dispatch cart:updated)
 *
 * Programmatic API (defined here, usable from any section script):
 *   RM.addToCart([{ id: 123, quantity: 1, properties: {} }, …], { source: 'routine-builder', openDrawer: true }) -> Promise<cart>
 *
 * Events: dispatches cart:updated { cart, sections, source } on document, then cart:open (unless disabled).
 * Inside the quick-view modal, the modal closes after a successful add.
 */
(() => {
  const RM = (window.RM = window.RM || {});

  const requestBody = (extra) => ({ sections: 'cart-drawer', sections_url: window.location.pathname, ...extra });

  /**
   * @param {Array<{id: number|string, quantity?: number, properties?: object, selling_plan?: number|string}>} items
   * @param {{source?: string, openDrawer?: boolean}} [options]
   */
  RM.addToCart = async (items, { source = 'api', openDrawer = true } = {}) => {
    const drawerMode = RM.config.cartType !== 'page';
    const payload = { items: items.map((item) => ({ quantity: 1, ...item })) };
    if (drawerMode) Object.assign(payload, requestBody({}));
    const result = await RM.fetchJSON(RM.config.routes.cartAdd, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return finish(result, source, openDrawer);
  };

  const finish = async (result, source, openDrawer) => {
    const cart = await RM.fetchJSON(RM.config.routes.cartJson);
    document.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: { cart, sections: result.sections, source },
    }));
    RM.announce(RM.config.strings.added);
    if (openDrawer) RM.openDrawer();
    return cart;
  };

  class ProductForm extends HTMLElement {
    #abort = new AbortController();

    connectedCallback() {
      this.form = this.querySelector('form');
      this.submitter = this.querySelector('[type="submit"]');
      this.form?.addEventListener('submit', (event) => this.#onSubmit(event), { signal: this.#abort.signal });
    }

    disconnectedCallback() {
      this.#abort.abort();
    }

    async #onSubmit(event) {
      event.preventDefault();
      if (!this.form || this.submitter?.getAttribute('aria-disabled') === 'true' || this.submitter?.disabled) return;
      this.#setBusy(true);
      this.#error('');

      const body = new FormData(this.form);
      const drawerMode = RM.config.cartType !== 'page';
      if (drawerMode) {
        body.append('sections', 'cart-drawer');
        body.append('sections_url', window.location.pathname);
      }

      try {
        const response = await fetch(RM.config.routes.cartAdd, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
          body,
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || RM.config.strings.addError);
        }
        // Close a surrounding quick-view modal first so the drawer takes focus.
        document.dispatchEvent(new CustomEvent('quickview:close'));
        await finish(result, 'product-form', !this.hasAttribute('data-no-drawer'));
      } catch (error) {
        this.#error(error.message || RM.config.strings.addError);
        document.dispatchEvent(new CustomEvent('cart:error', { detail: { message: error.message } }));
      } finally {
        this.#setBusy(false);
      }
    }

    #setBusy(busy) {
      if (!this.submitter) return;
      this.submitter.setAttribute('aria-busy', String(busy));
      if (busy) {
        this.submitter.setAttribute('aria-disabled', 'true');
      } else {
        this.submitter.removeAttribute('aria-disabled');
        this.submitter.removeAttribute('aria-busy');
      }
    }

    #error(message) {
      let box = this.querySelector('[data-product-form-error]');
      if (!box && message) {
        box = document.createElement('p');
        box.className = 'rm-error';
        box.setAttribute('role', 'alert');
        box.setAttribute('data-product-form-error', '');
        this.form?.append(box);
      }
      if (box) {
        box.textContent = message;
        box.hidden = !message;
      }
    }
  }

  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
})();
